/**
 * SECURITY: Secure localStorage Encryption
 * 
 * This utility provides AES-GCM encryption for localStorage data using the Web Crypto API.
 * All data is encrypted before storage and decrypted when retrieved.
 */

import { logger } from './logger';

class SecureStorage {
  private static readonly ALGORITHM = 'AES-GCM';
  private static readonly KEY_LENGTH = 256;
  private static readonly IV_LENGTH = 12; // 96 bits for GCM
  private static encryptionKey: CryptoKey | null = null;
  private static reencryptingKeys: Set<string> = new Set(); // Track keys being re-encrypted
  
  /**
   * Generate or retrieve the encryption key
   * Uses a combination of domain and a static salt for key derivation
   */
  private static async getEncryptionKey(): Promise<CryptoKey> {
    if (this.encryptionKey) {
      return this.encryptionKey;
    }
    
    try {
      // Create a deterministic key based on domain and static salt
      const domain = window.location.hostname;
      const staticSalt = 'lovable-secure-storage-2024';
      const keyMaterial = domain + staticSalt;
      
      // Import the key material
      const importedKey = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(keyMaterial),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );
      
      // Derive the actual encryption key using PBKDF2
      const salt = new TextEncoder().encode('secure-localStorage-salt-2024');
      this.encryptionKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        importedKey,
        {
          name: this.ALGORITHM,
          length: this.KEY_LENGTH
        },
        false,
        ['encrypt', 'decrypt']
      );
      
      return this.encryptionKey;
    } catch (error) {
      logger.error('Failed to generate encryption key:', error);
      throw new Error('Encryption key generation failed');
    }
  }
  
  /**
   * Encrypt data using AES-GCM
   */
  private static async encrypt(data: any): Promise<string> {
    try {
      const key = await this.getEncryptionKey();
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));
      const encodedData = new TextEncoder().encode(JSON.stringify(data));
      
      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        encodedData
      );
      
      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);
      
      // Convert to base64 for storage
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      logger.error('Encryption failed:', error);
      throw new Error('Data encryption failed');
    }
  }
  
  /**
   * Decrypt data using AES-GCM
   */
  private static async decrypt(encryptedData: string): Promise<any> {
    try {
      const key = await this.getEncryptionKey();
      
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );
      
      // Extract IV and encrypted data
      const iv = combined.slice(0, this.IV_LENGTH);
      const data = combined.slice(this.IV_LENGTH);
      
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        data
      );
      
      const decodedData = new TextDecoder().decode(decryptedData);
      return JSON.parse(decodedData);
    } catch (error) {
      logger.error('Decryption failed:', error);
      throw new Error('Data decryption failed');
    }
  }
  
  /**
   * Set an item in localStorage with encryption
   */
  static async setItem(key: string, value: any): Promise<void> {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
        return;
      }
      
      const encryptedValue = await this.encrypt(value);
      
      localStorage.setItem(key, encryptedValue);
      
      // Only verify storage for critical keys, not all keys
      if (key.includes('session') || key.includes('auth') || key.includes('dealer')) {
        const verification = localStorage.getItem(key);
        if (!verification) {
          console.error(`❌ SECURE_STORAGE: Storage verification FAILED for critical key: ${key}`);
        }
      }
      
      logger.log(`🔐 Securely stored encrypted data for key: ${key}`);
    } catch (error) {
      console.error(`❌ SECURE_STORAGE: Failed to securely store data for key ${key}:`, error);
      logger.error(`Failed to securely store data for key ${key}:`, error);
      // Fallback to regular localStorage for non-critical data
      localStorage.setItem(key, JSON.stringify(value));
    }
  }
  
  /**
   * Get an item from localStorage with decryption
   */
  static async getItem(key: string): Promise<any> {
    try {
      const item = localStorage.getItem(key);
      
      if (!item) {
        return null;
      }
      
      // Try to decrypt - if it fails, assume it's unencrypted data
      try {
        const decryptedValue = await this.decrypt(item);
        logger.log(`🔓 Successfully decrypted data for key: ${key}`);
        return decryptedValue;
      } catch (decryptError) {
        // If decryption fails, try to parse as regular JSON (backward compatibility)
        try {
          const jsonValue = JSON.parse(item);
          
          // Re-encrypt and save the data to prevent future decryption failures
          // Only if we're not already in a re-encryption process for this key
          if (!this.reencryptingKeys.has(key)) {
            this.reencryptingKeys.add(key);
            try {
              await this.setItem(key, jsonValue);
            } finally {
              this.reencryptingKeys.delete(key);
            }
          }
          
          return jsonValue;
        } catch (parseError) {
          // If JSON parsing also fails, return the raw string
          return item;
        }
      }
    } catch (error) {
      console.error(`❌ SECURE_STORAGE: Failed to retrieve data for key ${key}:`, error);
      logger.error(`Failed to retrieve data for key ${key}:`, error);
      return null;
    }
  }
  
  /**
   * Remove an item from localStorage
   */
  static removeItem(key: string): void {
    localStorage.removeItem(key);
    logger.log(`🗑️ Removed item from secure storage: ${key}`);
  }
  
  /**
   * Clear all localStorage items
   */
  static clear(): void {
    localStorage.clear();
    logger.log('🗑️ Cleared all secure storage');
  }
  
  /**
   * Check if Web Crypto API is available
   */
  static isAvailable(): boolean {
    return !!(crypto && crypto.subtle);
  }
  
  /**
   * Synchronous fallback methods (use regular localStorage)
   * These should only be used when async operations are not possible
   */
  static setItemSync(key: string, value: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      logger.warn(`⚠️ Used insecure sync storage for key: ${key} (crypto not available)`);
    } catch (error) {
      logger.error(`Failed to store data synchronously for key ${key}:`, error);
    }
  }
  
  static getItemSync(key: string): any {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      logger.error(`Failed to retrieve data synchronously for key ${key}:`, error);
      return localStorage.getItem(key); // Return raw string as fallback
    }
  }
}

export default SecureStorage;